import { useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Calendar as CalendarIcon, Search, AlertCircle, Info } from "lucide-react"
import { format, startOfDay } from "date-fns"
import { de } from "date-fns/locale"
import { cn } from "@/lib/utils"
import { validateField, getInputErrorStyles } from "@/lib/validation"
import { CityAutocomplete } from "@/components/ui/CityAutocomplete"
import { GuestSelector, GuestData } from "@/components/ui/GuestSelector"
import { useDropOffTracking } from "@/hooks/useDropOffTracking"

const SearchForm = () => {
  const navigate = useNavigate()
  const { trackSearch } = useDropOffTracking()
  const checkoutRef = useRef<HTMLButtonElement>(null)
  const today = startOfDay(new Date())
  
  const [searchData, setSearchData] = useState({
    destination: "",
    checkin: null as Date | null,
    checkout: null as Date | null,
    guests: { adults: 1, children: [] } as GuestData
  })
  
  const [flexible, setFlexible] = useState(false)
  const [flexibleDays, setFlexibleDays] = useState(false)
  
  const [isCheckinOpen, setIsCheckinOpen] = useState(false)
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)
  
  const [touched, setTouched] = useState({
    destination: false,
    checkin: false,
    checkout: false,
    guests: false
  })

  const [errors, setErrors] = useState({
    destination: [] as string[],
    checkin: [] as string[],
    checkout: [] as string[],
    guests: [] as string[]
  })

  const validateForm = () => {
    // If flexible is checked, destination and dates are optional
    const destinationRequired = !flexible
    const datesRequired = !flexible
    
    const newErrors = {
      destination: validateField(searchData.destination, { required: destinationRequired }, "Reiseziel").errors,
      checkin: validateField(searchData.checkin, { required: datesRequired }, "Anreisedatum").errors,
      checkout: validateField(searchData.checkout, { 
        required: datesRequired,
        custom: (value) => {
          if (searchData.checkin && value && new Date(value) <= new Date(searchData.checkin)) {
            return "Abreisedatum muss nach dem Anreisedatum liegen"
          }
          return null
        }
      }, "Abreisedatum").errors,
      guests: [] as string[] // GuestSelector handles its own validation
    }
    
    setErrors(newErrors)
    return Object.values(newErrors).every(fieldErrors => fieldErrors.length === 0)
  }

  const handleFieldBlur = (field: keyof typeof touched) => {
    setTouched(prev => ({ ...prev, [field]: true }))
    
    // If flexible is checked, destination and dates are optional
    const destinationRequired = !flexible
    const datesRequired = !flexible
    
    // Validate just this field
    const fieldValidation = {
      destination: () => validateField(searchData.destination, { required: destinationRequired }, "Reiseziel"),
      checkin: () => validateField(searchData.checkin, { required: datesRequired }, "Anreisedatum"),
      checkout: () => validateField(searchData.checkout, { 
        required: datesRequired,
        custom: (value) => {
          if (searchData.checkin && value && new Date(value) <= new Date(searchData.checkin)) {
            return "Abreisedatum muss nach dem Anreisedatum liegen"
          }
          return null
        }
      }, "Abreisedatum"),
      guests: () => ({ errors: [] }) // GuestSelector handles its own validation
    }
    
    const validation = fieldValidation[field]()
    setErrors(prev => ({ ...prev, [field]: validation.errors }))
  }

  const handleCheckinSelect = (date: Date | undefined) => {
    setSearchData(prev => ({ ...prev, checkin: date || null }))
    setIsCheckinOpen(false)
    
    // Automatically open checkout picker after checkin selection
    if (date) {
      setTimeout(() => {
        setIsCheckoutOpen(true)
      }, 100)
    }
  }

  const handleCheckoutSelect = (date: Date | undefined) => {
    setSearchData(prev => ({ ...prev, checkout: date || null }))
    setIsCheckoutOpen(false)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Mark all fields as touched
    setTouched({
      destination: true,
      checkin: true,
      checkout: true,
      guests: true
    })
    
    if (!validateForm()) {
      return
    }
    
    // Track the search interaction
    const totalGuests = searchData.guests.adults + searchData.guests.children.length
    const toDateParam = (d: Date) => format(startOfDay(d), 'yyyy-MM-dd')

    trackSearch({
      destination: searchData.destination || undefined,
      checkin: searchData.checkin ? toDateParam(searchData.checkin) : undefined,
      checkout: searchData.checkout ? toDateParam(searchData.checkout) : undefined,
      guests: totalGuests,
      searchType: 'main'
    })

    // Create URL params from search data
    const params = new URLSearchParams()
    if (searchData.destination) params.set('destination', searchData.destination)
    if (searchData.checkin) params.set('checkin', toDateParam(searchData.checkin))
    if (searchData.checkout) params.set('checkout', toDateParam(searchData.checkout))

    params.set('guests', totalGuests.toString())
    params.set('adults', searchData.guests.adults.toString())
    if (searchData.guests.children.length > 0) {
      params.set('children', JSON.stringify(searchData.guests.children))
    }

    if (flexible) params.set('flexible', 'true')
    if (flexibleDays) params.set('flexibleDays', 'true')

    // Navigate to listings page with search parameters
    navigate(`/listings?${params.toString()}`)
  }
  return (
    <form onSubmit={handleSearch}>
      <div className="bg-white rounded-2xl shadow-strong p-6 md:p-8 w-full max-w-4xl mx-auto border border-border/10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        {/* Reiseziel */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Wohin?</label>
          <CityAutocomplete
            placeholder="Reiseziel eingeben"
            value={searchData.destination}
            onChange={(value) => setSearchData({...searchData, destination: value})}
            onBlur={() => handleFieldBlur('destination')}
            className={getInputErrorStyles(touched.destination && errors.destination.length > 0)}
            hasError={touched.destination && errors.destination.length > 0}
          />
          {touched.destination && errors.destination.length > 0 && (
            <div className="space-y-1">
              {errors.destination.map((error, index) => (
                <p key={index} className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {error}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Check-in */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Anreise</label>
          <Popover open={isCheckinOpen} onOpenChange={setIsCheckinOpen}>
            <PopoverTrigger asChild>
              <div className="relative">
                <Button
                  variant="outline"
                  className={cn(
                    "h-12 w-full justify-start text-left font-normal border-0 bg-muted/30 hover:bg-white transition-smooth pl-10",
                    !searchData.checkin && "text-muted-foreground",
                    getInputErrorStyles(touched.checkin && errors.checkin.length > 0)
                  )}
                >
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  {searchData.checkin ? (
                    format(searchData.checkin, "dd.MM.yyyy", { locale: de })
                  ) : (
                    <span>Datum wählen</span>
                  )}
                </Button>
                {touched.checkin && errors.checkin.length > 0 && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-destructive" />
                )}
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={searchData.checkin || undefined}
                onSelect={handleCheckinSelect}
                disabled={(date) => date < today}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          {touched.checkin && errors.checkin.length > 0 && (
            <div className="space-y-1">
              {errors.checkin.map((error, index) => (
                <p key={index} className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {error}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Check-out */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Abreise</label>
          <Popover open={isCheckoutOpen} onOpenChange={setIsCheckoutOpen}>
            <PopoverTrigger asChild>
              <div className="relative">
                <Button
                  ref={checkoutRef}
                  variant="outline"
                  className={cn(
                    "h-12 w-full justify-start text-left font-normal border-0 bg-muted/30 hover:bg-white transition-smooth pl-10",
                    !searchData.checkout && "text-muted-foreground",
                    getInputErrorStyles(touched.checkout && errors.checkout.length > 0)
                  )}
                >
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  {searchData.checkout ? (
                    format(searchData.checkout, "dd.MM.yyyy", { locale: de })
                  ) : (
                    <span>Datum wählen</span>
                  )}
                </Button>
                {touched.checkout && errors.checkout.length > 0 && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-destructive" />
                )}
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={searchData.checkout || undefined}
                onSelect={handleCheckoutSelect}
                disabled={(date) => 
                  date < today || 
                  (searchData.checkin && date <= searchData.checkin)
                }
                defaultMonth={searchData.checkin || undefined}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          {touched.checkout && errors.checkout.length > 0 && (
            <div className="space-y-1">
              {errors.checkout.map((error, index) => (
                <p key={index} className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {error}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Gäste */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Gäste</label>
          <GuestSelector
            value={searchData.guests}
            onChange={(guests) => setSearchData({...searchData, guests})}
            compact={true}
          />
        </div>
      </div>

        {/* Flexible Date Options */}
        <TooltipProvider>
          <div className="flex flex-wrap gap-6 mt-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="flexible" 
                checked={flexible}
                onCheckedChange={(checked) => setFlexible(checked === true)}
              />
              <label 
                htmlFor="flexible" 
                className="text-sm text-muted-foreground cursor-pointer select-none"
              >
                Ich bin flexibel
              </label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Ermöglicht die Suche ohne festes Datum oder Reiseziel. Ideal um alle verfügbaren Angebote zu entdecken oder nach den günstigsten Unterkünften zu suchen.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="flexibleDays" 
                checked={flexibleDays}
                onCheckedChange={(checked) => setFlexibleDays(checked === true)}
                disabled={flexible && !searchData.checkin}
              />
              <label 
                htmlFor="flexibleDays" 
                className={cn(
                  "text-sm text-muted-foreground cursor-pointer select-none",
                  flexible && !searchData.checkin && "opacity-50"
                )}
              >
                ±3 Tage
              </label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Erweitert die Suche um 3 Tage vor und nach den gewählten Daten. So finden Sie mehr verfügbare Unterkünfte, auch wenn Ihre Wunschtermine bereits ausgebucht sind.</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </TooltipProvider>

        <div className="mt-6 flex flex-col items-center gap-4">
          <Button type="submit" variant="hero" size="xl" className="px-12">
            <Search className="h-5 w-5 mr-2" />
            Unterkünfte suchen
          </Button>
          
          {/* Link to Advanced Search */}
          <div className="text-center">
            <Button 
              variant="link" 
              onClick={() => navigate('/powerful-search')}
              className="text-sm text-muted-foreground hover:text-primary"
            >
              🚀 Erweiterte Suche mit KI-Unterstützung
            </Button>
            <p className="text-xs text-muted-foreground mt-1">
              Tippfehlertoleranz • Intelligente Filter • Personalisierte Vorschläge
            </p>
          </div>
        </div>
      </div>
    </form>
  )
}

export default SearchForm